import {Component, DestroyRef, EventEmitter, inject, Input, Output, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {FreemiumService} from '../../../services/freemium.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TokenService} from '../../../services/token.service';
import {UtilService} from '../../../services/util.service';
import {catchError, finalize, tap, throwError } from 'rxjs';
import {HubspotFirstPhase, HubspotFormPhase} from '../../../services/hubspot/hubspot.types';
import {PosthogEventName} from '../../../services/types';
import {PosthogService} from '../../../services/posthog.service';
import {AUTH0_ROLE} from "../../../shared/types/UserProfile";
import {AssetVersionPipe} from '../../../pipes/asset-version.pipe';
import {AuthService} from '@auth0/auth0-angular';

const USER_EXISTS_ERROR_MESSAGE = 'The user already exists';
const WORK_EMAIL_REQUIRED_ERROR_MESSAGE = 'Email must be a work email address';

@Component({
  selector: 'app-register-trial-email',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AssetVersionPipe],
  templateUrl: './register-trial-email.component.html',
  styleUrl: './register-trial-email.component.scss',
})
export class RegisterTrialEmailComponent {
  private freemiumService = inject(FreemiumService);
  private tokenService = inject(TokenService);
  private destroyRef = inject(DestroyRef);
  private utilService = inject(UtilService);
  private posthogService = inject(PosthogService);
  private authService = inject(AuthService);

  public googleControl = new FormControl<string | null>('Continue with Google');
  public formMainTitle: string = '';
  public formThirdTitle: string = '';

  public isLoading = false;
  
  @Input() firstNameControl!: FormControl<string | null>;
  @Input() lastNameControl!: FormControl<string | null>;
  @Input() emailControl!: FormControl<string | null>;
  @Input() enforceGoogleSignup = false;
  @Output() nextStep = new EventEmitter<void>();
  @Output() googleSignupSuccess = new EventEmitter<void>();

  ngOnInit(): void {
    this.formMainTitle = this.enforceGoogleSignup ? 'Complete Your\nFree Trial Sign-Up' : "See your competitors' full\nmarketing playbook";
    this.formThirdTitle = this.enforceGoogleSignup ? 'Continue where you left off' : 'Start your free 14-day trial. No credit card required.';
  }

  getEmailErrorMessage(): string {
    if (this.emailControl.hasError('required')) {
      return 'Email is required';
    }
    if (this.emailControl.hasError('email')) {
      return 'Please enter a valid email address';
    }
    if (this.emailControl.hasError('forbiddenDomain')) {
      return 'Please use a work email address';
    }
    if (this.emailControl.hasError('userExists')) {
      return this.emailControl.getError('userExists');
    }
    return '';
  }

  async onSubmit(): Promise<void> {
    if (!this.firstNameControl.valid || !this.lastNameControl.valid || !this.emailControl.valid) {
      this.firstNameControl.markAsTouched();
      this.lastNameControl.markAsTouched();
      this.emailControl.markAsTouched();
      return;
    }

    this.isLoading = true;

    const payload = {
      email: this.emailControl.value!,
      firstName: this.firstNameControl.value!,
      lastName: this.lastNameControl.value!
    };

    this.freemiumService.updateHubspotFields({
      email: payload.email,
      firstname: payload.firstName,
      lastname: payload.lastName
    });

    const hubspotPayload : HubspotFirstPhase = {
      email: payload.email,
      firstname: payload.firstName,
      lastname: payload.lastName
    }

    this.freemiumService.signup(payload)
      .pipe(
        this.utilService.retryWithCondition(
          (error) => error?.error?.message !== USER_EXISTS_ERROR_MESSAGE
        ),
        tap((response) => {
          if (response) {
            if (response.id) {
              Object.assign(hubspotPayload, {userId: response.id});
            }
            if (response.signupToken) {
              Object.assign(hubspotPayload, {signupToken: response.signupToken});
            }
          }
        }),
        catchError((error) => {
          this.isLoading = false;
          if (error?.error?.message === USER_EXISTS_ERROR_MESSAGE) {
            this.emailControl.setErrors({userExists: error.error.message});
            this.emailControl.markAsTouched();
          }
          return throwError(() => error);
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(async () => {
          return this.freemiumService.sendToHubSpot(HubspotFormPhase.STEP_ONE, hubspotPayload, window.location.href)
            .catch(error => console.error('Error sending to HubSpot:', error));
        })
      )
      .subscribe({
        next: async (response) => {
          if (response.token.access_token) {
            this.tokenService.setToken(response.token.access_token);
          }
          this.isLoading = false;
          this.nextStep.emit();
          this.posthogService.identify({
            id: response.id,
            email: response.email,
            firstName: response.firstName,
            lastName: response.lastName,
            role: AUTH0_ROLE.TRIAL,
            account: 'temp',
            trialDaysLeft: 14
          })
          this.posthogService.capture(PosthogEventName.SIGN_UP_SUCCEEDED, {'sign_up_method': 'email_and_password'});
        }
      });
  }

  onGoogleSignup(): void {
    this.posthogService.capture(PosthogEventName.CONTINUE_WITH_GOOGLE_BUTTON_CLICKED);
    this.authService.loginWithPopup({
      authorizationParams: {
        connection: 'google-oauth2',
        flow_type: 'trial_signup',
        scope: 'openid profile email'
      }
    }).subscribe({
      next: () => {
        this.googleSignupSuccess.emit();
      },
      error: (error) => {
        if (error.message === WORK_EMAIL_REQUIRED_ERROR_MESSAGE) {
          this.googleControl.setErrors({workEmailRequired: 'Please use a work email address'});
          this.posthogService.capture(PosthogEventName.SIGN_UP_FAILED, {'sign_up_method': 'google_sso', 'reason': 'please use a business email'});
        } else {
          this.posthogService.capture(PosthogEventName.SIGN_UP_FAILED, {'sign_up_method': 'google_sso', 'reason': error.message});
        }
      }
    });
  }

  sendPostHogLoginLinkEvent() {
    this.posthogService.capture(PosthogEventName.LOG_IN_LINK_CLICKED);
  }

  openPolicy(){
    window.open('https://browsi.com/privacy-policy-2/', '_blank');
  }

  openTermsOfService(){
    window.open('https://browsi.com/terms-of-use/', '_blank');
  }
}
